import { SceneName } from "@features/gfx/generator/generator"
import { useEffect, useRef } from "react"
import { proxy, useSnapshot } from "valtio"

/**
 * Stores common application state — background scene, menu state, footer visibility, etc.
 */
const store = proxy({
  menuIsOpen: false,
  background: {
    scene: "idle" as SceneName,
    darken: 0,
  } as BackgroundState,
  footerVisible: false,
  openMenu() {
    store.menuIsOpen = true
  },
  closeMenu() {
    store.menuIsOpen = false
  },
  setBackgroundState(state: BackgroundState) {
    store.background = state
  },
  setFooterVisible(visible: boolean) {
    store.footerVisible = visible
  },
  servicesVisible: false,
  setServicesVisible(visible: boolean) {
    store.servicesVisible = visible
  },
})

/**
 * Provides reactive access to the application state.
 * If any values change, components using those values will re-render.
 */
export function useAppState() {
  return useSnapshot(store)
}

export type BackgroundState = {
  scene: SceneName
  darken: number
}

/**
 * Can be used to set the initial background state on page load, as well as get reactive access as values change, and to optionally update it in response to interaction.
 */
export function useBackground(init?: BackgroundState): [BackgroundState, (state: BackgroundState) => void] {
  const firstRender = useRef(true)

  if (firstRender.current && init) {
    store.background = init
  }

  const snapshot = useSnapshot(store)

  firstRender.current = false

  useEffect(() => {
    if (init) {
      store.background = init
    }
  }, [init?.scene, init?.darken])

  return [snapshot.background, snapshot.setBackgroundState]
}
